<template>
  <div>
    <AlertDismiss ref="alertDismiss"></AlertDismiss>
    <form @submit.prevent="onSubmit">
      <div class="row">
        <label class="mb-3">
          Motivo de la reunión:
          <input
            type="text"
            v-model="form.summary"
            :class="getClass(form.summary)"
            placeholder="Ingrese el motivo de la reunión"
            required
          >
        </label>

        <label class="mb-3">
          Descripción:
          <textarea
            v-model="form.description"
            :class="getClass(form.description)"
            placeholder="Ingrese la descripción de la reunión"
            required
          ></textarea>
        </label>

        <div class="col-sm-6 mb-3 d-flex gap-2">
          <label class="w-50">
            Fecha:
            <input
              type="date"
              v-model="form.startDate"
              class="form-control"
            >
          </label>
          <label class="w-50">
            Hora:
            <input
              type="time"
              v-model="form.startTime"
              class="form-control"
            >
          </label>
        </div>

        <label class="col-sm-6 mb-2">
          Duración:
          <select
            v-model="form.duration"
            :class="getClass(form.duration)"
            required
          >
            <option :value="null" disabled>-- Seleccione un valor ---</option>
            <option value="15">15 minutos</option>
            <option value="30">30 minutos</option>
            <option value="60">1 hora</option>
            <option value="120">2 horas</option>
            <option value="custom">Otro valor (min)</option>
          </select>

          <input
            v-if="customDuration"
            v-model="form.customDuration"
            :class="getClass(form.customDuration) + ' mt-2'"
            type="number"
            placeholder="Duración en minutos" 
            required
          >
        </label>

        <div class="row">
          <div class="col-6">
            <label class="form-label">¿La reunión es virtual?</label>
            <br>
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                type="radio"
                :value="true"
                v-model="form.isOnline"
              >
              <label class="form-check-label">Sí</label>
            </div>

            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                type="radio"
                :value="false"
                v-model="form.isOnline"
              >
              <label class="form-check-label">No</label>
            </div>
          </div>
          
          <div v-if="form.isOnline === false" class="col-6">
            <label class="form-label w-100">
              Dirección
              <input
                type="text"
                :class="getClass(form.address)"
                v-model="form.address"
                placeholder="Ingrese la dirección"
                required
              >
            </label>
          </div>
        </div>

        <div class="col mb-3">
          <MultiAutocomplete
            v-model="form.assistants"
            @add="addParticipant"
            @remove="removeParticipant"
            :items="mails"
            :placeholder="'Ingrese el email de los participantes'"
            label="Participantes"
            inputType="email"
          />
          <small v-if="noParticipants" class="text-danger">
            Debe agregar al menos un participante
          </small>
        </div>

        <div>
          <button type="submit" :disabled="isUploading" class="btn btn-outline-primary" @click="onSubmitClick">
            <b-spinner small v-if="isUploading" class="align-middle"></b-spinner>
            Registrar reunión
          </button>
        </div>
      </div>
    </form>

  </div>

</template>

<script>
import MultiAutocomplete from "@/components/shared/Autocomplete-input.vue";
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import {mapActions} from 'vuex';
import {EventNames, InitPageSize} from "@/plugins/enums";
import { addDays } from "@/plugins/complianceScripts";
import { formatDateInput } from "@/plugins/complianceScripts";

export default {
  name: "MeetingForm",
  components: {
    MultiAutocomplete,
    AlertDismiss
  },

  data() {
    return {
      form: {
        summary: "",
        description: "",
        startDate: formatDateInput((addDays(new Date(), 1))),
        startTime: this.currentHour(),
        duration: null,
        customDuration: null,
        assistants: [],
        isOnline: true,
        address: null
      },

      mails: [],

      attemptedSubmit: false,
      isUploading: false,
      partialEmail: "",
      showDropdown: false
    }
  },

  computed: {
    customDuration() {
      return this.form.duration === 'custom'
    },

    noParticipants() {
      return this.form.assistants.length === 0
    },
  },

  async mounted() {
    let result = await this.getAllMails()

    if (result.isError) {
      this.$refs.alertDismiss.showAlert(result.message)
      return
    }

    this.mails = result.data.map(x => ({
      name: x.mail,
      id: x.id,
    }))
  },

  methods: {
    addDays,

    formatDateInput,
    ...mapActions([
      "countUsers",
      'allUsersForPage',
      'createMeetingVirtual',
      'createMeetingPhysical',
      'getAllMails'
    ]),

    currentHour() {
      const date = new Date();
      const hours = String(date.getHours()).padStart(2, '0');
      return `${hours}:00`;
    },

    getClass(val) {
      let validation = this.attemptedSubmit && !val ? 'is-invalid' : ''
      return validation + ' form-control'
    },

    async onSubmitClick() {
        this.attemptedSubmit = true      
    },
    
    async onSubmit() {
      this.isUploading = true;
      
      let data = {
        summary: this.form.summary,
        description: this.form.description,
        dateStart: this.buildStartDate(),
        duration: this.form.duration === 'custom' ? this.form.customDuration : Number(this.form.duration),
        assistants: this.form.assistants.map(x => ({ mail: x.name})),
      }
      
      let result
      
      if (this.form.isOnline) {
        result = await this.createMeetingVirtual(data);
      } else {
        data.address = this.form.address;
        result = await this.createMeetingPhysical(data);
      }

      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, 'warning');
        this.isUploading = false;
        return;
      }

      this.$refs.alertDismiss.showAlert('Reunión creada correctamente', 'success');
      this.$eventBus.$emit(EventNames.CreateMeeting, {
        page: 1,
        pageSize: InitPageSize.MeetingPageSize
      });
      this.isUploading = false;

      this.clear()
    },

    addParticipant(participant) {
      this.form.assistants.push(participant);
    },

    removeParticipant(participant) {
      this.form.assistants.splice(this.form.assistants.indexOf(participant), 1);
    },

    buildStartDate() {
      let startDate = this.form.startDate
      if (startDate === undefined || startDate === null) {
        return null
      }

      return `${startDate}T${this.form.startTime}:00.000Z`;
    },

    clear() {
      this.form = {
        summary: "",
        description: "",
        startDate: formatDateInput(addDays(new Date(), 1)),
        startTime: this.currentHour(),
        duration: null,
        customDuration: null,
        assistants: [],
        isOnline: null,
        address: null
      }
      this.attemptedSubmit = false
    }
  }
}
</script>